import { Capacitor } from "@capacitor/core";

import type { Rule, RulesCapability, Target } from "../rules/ruleTypes";
import { getEnforcingRules } from "../rules/ruleSchedule";
import { resolveTargetDomains } from "../rules/websiteCategories";
import { NativeWebFilter, type WebFilterStatus } from "./plugins";

/** Both platforms run an on-device web filter (iOS NEPacketTunnel, Android VpnService). */
function isNativeWebFilterPlatform(): boolean {
  return (
    Capacitor.isNativePlatform() &&
    (Capacitor.getPlatform() === "ios" || Capacitor.getPlatform() === "android")
  );
}

const UNAVAILABLE_STATUS: WebFilterStatus = {
  status: "unavailable",
  installed: false,
  enabled: false,
  enforcedCount: 0,
  reason: "web_or_plugin_unavailable",
};

export type WebFilterOrchestrationAction =
  | "noop"
  | "sync_only"
  | "disable_and_sync"
  | "enable_and_sync"
  | "needs_install";

export type WebFilterOrchestrationPlan = {
  action: WebFilterOrchestrationAction;
  domains: string[];
};

export type WebFilterOrchestrationResult = {
  status: WebFilterStatus;
  /** True when enforcing web domains exist but the VPN profile is not installed. */
  needsVpnInstall: boolean;
};

/**
 * Effective blocked-domain set for the VPN extension: the union of every website
 * target referenced by a rule that is *enforcing right now* (enabled + today's
 * weekday + inside its window). Domains are normalized and deduped, and category
 * ids are expanded via `resolveTargetDomains` (which calls WEBSITE_CATEGORIES).
 *
 * Schedule is honored on the JS side because a packet tunnel has no native
 * per-rule scheduler — we sync the set that should be blocked at `now` and
 * re-sync as windows open/close.
 */
export function deriveActiveBlockedDomains(
  rules: Rule[],
  targets: Target[],
  now: Date = new Date(),
): string[] {
  const targetsById = new Map(targets.map((target) => [target._id, target]));
  const domains = new Set<string>();

  for (const rule of getEnforcingRules(rules, now)) {
    for (const targetId of rule.targetIds) {
      const target = targetsById.get(targetId);
      if (!target) continue;
      for (const domain of resolveTargetDomains(target)) {
        domains.add(domain);
      }
    }
  }

  return [...domains].sort();
}

/** Pure plan for VPN lifecycle: install-only when idle, enable+sync when blocking, disable when idle. */
export function planWebFilterOrchestration(
  domains: string[],
  status: WebFilterStatus,
): WebFilterOrchestrationPlan {
  if (domains.length === 0) {
    if (status.status === "active" || status.enabled) {
      return { action: "disable_and_sync", domains: [] };
    }
    if (status.installed) {
      return { action: "sync_only", domains: [] };
    }
    return { action: "noop", domains: [] };
  }

  if (!status.installed || status.status === "not_installed" || status.status === "denied") {
    return { action: "needs_install", domains };
  }

  if (status.status === "disabled" || !status.enabled) {
    return { action: "enable_and_sync", domains };
  }

  return { action: "sync_only", domains };
}

/** Map the native web-filter status onto the shared rules `webBlocking` capability. */
export function webBlockingCapabilityFromStatus(status: WebFilterStatus): RulesCapability {
  switch (status.status) {
    case "active":
      return { status: "active", enforcedCount: status.enforcedCount };
    case "disabled":
    case "not_installed":
      return {
        status: "permission_gated",
        reason: "vpn_profile_not_installed",
        enforcedCount: 0,
      };
    case "denied":
      return {
        status: "permission_gated",
        reason: "vpn_permission_denied",
        enforcedCount: 0,
      };
    case "unavailable":
    default:
      return {
        status: "unavailable",
        reason: status.reason ?? "web_filter_unavailable",
        enforcedCount: 0,
      };
  }
}

export async function getWebFilterStatus(): Promise<WebFilterStatus> {
  if (!isNativeWebFilterPlatform()) return UNAVAILABLE_STATUS;
  try {
    return await NativeWebFilter.getStatus();
  } catch (error) {
    console.error("NativeWebFilter.getStatus failed", error);
    return UNAVAILABLE_STATUS;
  }
}

/** Install the VPN profile without starting the tunnel (triggers the iOS add-VPN prompt). */
export async function installWebFilter(): Promise<WebFilterStatus> {
  if (!isNativeWebFilterPlatform()) return UNAVAILABLE_STATUS;
  try {
    return await NativeWebFilter.install();
  } catch (error) {
    console.error("NativeWebFilter.install failed", error);
    return UNAVAILABLE_STATUS;
  }
}

/** Install (if needed) + enable the local VPN profile and start the tunnel. */
export async function enableWebFilter(): Promise<WebFilterStatus> {
  if (!isNativeWebFilterPlatform()) return UNAVAILABLE_STATUS;
  try {
    return await NativeWebFilter.enable();
  } catch (error) {
    console.error("NativeWebFilter.enable failed", error);
    return UNAVAILABLE_STATUS;
  }
}

export async function disableWebFilter(): Promise<WebFilterStatus> {
  if (!isNativeWebFilterPlatform()) return UNAVAILABLE_STATUS;
  try {
    return await NativeWebFilter.disable();
  } catch (error) {
    console.error("NativeWebFilter.disable failed", error);
    return UNAVAILABLE_STATUS;
  }
}

async function pushBlockedDomains(domains: string[]): Promise<WebFilterStatus> {
  try {
    return await NativeWebFilter.syncBlockedDomains({ domains });
  } catch (error) {
    console.error("NativeWebFilter.syncBlockedDomains failed", error);
    return UNAVAILABLE_STATUS;
  }
}

/**
 * Align VPN lifecycle with the schedule-active blocked-domain set: tunnel on only
 * when the set is non-empty and the profile is installed; tunnel off when empty.
 * Returns `needsVpnInstall` when blocking is required but the profile is missing.
 */
export async function orchestrateNativeWebFilter(
  rules: Rule[],
  targets: Target[],
  now: Date = new Date(),
): Promise<WebFilterOrchestrationResult> {
  if (!isNativeWebFilterPlatform()) {
    return { status: UNAVAILABLE_STATUS, needsVpnInstall: false };
  }

  const domains = deriveActiveBlockedDomains(rules, targets, now);
  const status = await getWebFilterStatus();
  const plan = planWebFilterOrchestration(domains, status);

  switch (plan.action) {
    case "noop":
      return { status, needsVpnInstall: false };
    case "needs_install":
      return { status, needsVpnInstall: true };
    case "disable_and_sync": {
      const disabled = await disableWebFilter();
      const synced = await pushBlockedDomains([]);
      return { status: synced.status === "unavailable" ? disabled : synced, needsVpnInstall: false };
    }
    case "enable_and_sync": {
      const enabled = await enableWebFilter();
      if (enabled.status !== "active" && !enabled.enabled) {
        return { status: enabled, needsVpnInstall: false };
      }
      const synced = await pushBlockedDomains(plan.domains);
      return { status: synced, needsVpnInstall: false };
    }
    case "sync_only": {
      const synced = await pushBlockedDomains(plan.domains);
      return { status: synced, needsVpnInstall: false };
    }
  }
}

/**
 * @deprecated Prefer `orchestrateNativeWebFilter` — this wrapper keeps call sites working.
 */
export async function syncNativeWebFilter(
  rules: Rule[],
  targets: Target[],
  now: Date = new Date(),
): Promise<WebFilterStatus> {
  const result = await orchestrateNativeWebFilter(rules, targets, now);
  return result.status;
}
